'use client'

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { usePathname, useRouter } from 'next/navigation'
import { QrCode, X, Copy, Check, Loader2, Tv, Camera, ArrowLeft, Keyboard } from 'lucide-react'
import { useParty } from '@/lib/party-context'
import QrScanner from './QrScanner'
import { isCompactScreen } from '@/lib/screen'

/**
 * Bouton « Partager sur un écran » (ex-Baffle).
 *
 * Deux usages, un seul bouton :
 *   - MODE TV  (host)  : CET écran (ordi / télé) devient la baffle. On affiche un
 *     QR + un code ; un téléphone scanne pour envoyer son / vidéos ici.
 *   - MODE SCAN (guest): sur un TÉLÉPHONE / TABLETTE, on ouvre la CAMÉRA pour
 *     scanner le QR affiché sur une TV / un ordi → on rejoint cet écran et on
 *     lui envoie le contenu.
 *
 * Le mode par défaut s'adapte à l'appareil : tactile/mobile → SCAN, ordi/TV → TV.
 * Couleurs : accent violet BLC Médias (Musique comme Vidéo).
 *
 * Le bouton déclencheur reste stylable (className/style/children) : mini-lecteur,
 * sidebar, accueil…
 */

type Mode = 'tv' | 'scan'

export default function BaffleConnect({
  className, style, children, title = 'Partager sur un écran (TV)',
}: {
  className?: string
  style?: CSSProperties
  children?: ReactNode
  title?: string
}) {
  const { role, code, becomeHost } = useParty()
  const router = useRouter()
  const pathname = usePathname()
  const isVideo = !!pathname?.startsWith('/app/video')

  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<Mode>('tv')
  const [busy, setBusy] = useState(false)
  const [theCode, setTheCode] = useState<string | null>(code)
  const [qr, setQr] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [scanErr, setScanErr] = useState(false)

  // Accent = palette violette BLC Médias (identique en Musique et en Vidéo ;
  // seule la lueur est un peu plus bleutée côté Vidéo).
  const accent = isVideo
    ? { from: 'rgb(var(--a1))', to: 'rgb(var(--a2))', ring: 'rgb(var(--a2)/0.35)', glow: 'rgb(var(--a1)/0.30)', text: 'rgb(var(--a4))' }
    : { from: 'rgb(var(--a1))', to: 'rgb(var(--a3))', ring: 'rgb(var(--a3)/0.35)', glow: 'rgb(var(--a2)/0.30)', text: 'rgb(var(--a4))' }

  const fetchQr = (c: string) => {
    fetch('/api/party/create', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hostName: 'Écran', code: c }),
    }).then(r => r.json()).then(j => { if (j?.qr) setQr(j.qr) }).catch(() => {})
  }

  // Rend CET écran hôte (baffle) et récupère le QR à faire scanner.
  const startHost = async () => {
    if (role === 'host' && code) { setTheCode(code); if (!qr) fetchQr(code); return }
    setBusy(true)
    try {
      const r = await fetch('/api/party/create', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hostName: 'Écran' }),
      })
      const j = await r.json()
      if (j?.ok && j.code) {
        becomeHost(j.code, 'Écran')
        setTheCode(j.code)
        setQr(j.qr || null)
        if (!j.qr) fetchQr(j.code)
      }
    } catch {}
    setBusy(false)
  }

  // Détecte l'appareil pour choisir le mode par défaut à l'ouverture.
  const onClick = () => {
    let wantScan = false
    try {
      const isTv = document.documentElement.classList.contains('tv')
      const coarse = window.matchMedia?.('(pointer: coarse)')?.matches
      const small = isCompactScreen()
      wantScan = !isTv && (!!coarse || small)  // téléphone / tablette → scanner une TV
    } catch {}
    const m: Mode = wantScan ? 'scan' : 'tv'
    setMode(m)
    setScanErr(false)
    setOpen(true)
    if (m === 'tv') startHost()
  }

  const switchMode = (m: Mode) => {
    setMode(m)
    setScanErr(false)
    if (m === 'tv') startHost()
  }

  // Résultat du scan : le QR encode …/app/join?room=CODE. On extrait le code et
  // on ouvre la page « Rejoindre » (logique déjà testée) pour envoyer sur l'écran.
  const onScan = (text: string) => {
    let codeFound = ''
    try {
      const u = new URL(text)
      codeFound = (u.searchParams.get('room') || '').trim()
    } catch {}
    if (!codeFound) {
      const m = text.match(/room=([A-Za-z0-9]{3,8})/) || text.match(/\b([A-Za-z0-9]{4})\b/)
      if (m) codeFound = m[1]
    }
    codeFound = codeFound.toUpperCase()
    setOpen(false)
    if (/^[A-Z0-9]{3,8}$/.test(codeFound)) router.push(`/app/join?room=${codeFound}`)
    else router.push('/app/join')
  }

  const joinUrl = theCode && typeof location !== 'undefined' ? `${location.origin}/app/join?room=${theCode}` : ''
  const copy = () => { try { navigator.clipboard?.writeText(joinUrl) } catch {}; setCopied(true); setTimeout(() => setCopied(false), 1500) }

  return (
    <>
      <button onClick={onClick} title={title} aria-label={title} className={className} style={style}>
        {children ?? <QrCode size={16} />}
      </button>

      {open && typeof document !== 'undefined' && createPortal(
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 90,
            background: 'rgb(var(--sbg)/0.78)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}>
          <div onClick={e => e.stopPropagation()}
            className="glass-solid bg-spanel"
            style={{
              width: 400, maxWidth: '100%', borderRadius: 24, padding: 22, background: 'rgb(var(--spanel))',
              border: `1px solid ${accent.ring}`,
              boxShadow: `0 20px 60px rgba(0,0,0,0.6), 0 0 30px ${accent.glow}`,
              textAlign: 'center', position: 'relative',
            }}>
            <button onClick={() => setOpen(false)} title="Fermer"
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/[0.07] border border-white/10 hover:bg-white/[0.14] flex items-center justify-center text-white/75">
              <X size={16} />
            </button>

            <div className="text-[10px] font-semibold tracking-[0.28em] text-white/50 mb-1.5">BLC MÉDIAS</div>
            <div className="inline-flex items-center gap-2 text-xl font-extrabold tracking-tight mb-1 text-white">
              <Tv size={20} style={{ color: accent.text }} /> Partager sur un écran
            </div>

            {/* Bascule des deux modes */}
            <div className="flex gap-1.5 justify-center mt-3 mb-4">
              <ModeTab active={mode === 'tv'} onClick={() => switchMode('tv')} accent={accent} icon={<Tv size={14} />} label="Cet écran = TV" />
              <ModeTab active={mode === 'scan'} onClick={() => switchMode('scan')} accent={accent} icon={<Camera size={14} />} label="Scanner une TV" />
            </div>

            {mode === 'tv' ? (
              <>
                <p className="text-sm text-white/65 leading-relaxed mb-4">
                  Cet écran devient la <b>baffle / télé</b>. Scanne ce QR avec ton téléphone
                  pour lui envoyer <b>musiques et vidéos</b>.
                </p>
                <div className="bg-white rounded-2xl p-2 inline-flex items-center justify-center shadow-[0_10px_30px_rgba(0,0,0,.45)]" style={{ width: 208, height: 208 }}>
                  {qr
                    ? <img src={qr} alt="QR" style={{ width: 192, height: 192 }} />
                    : <div className="flex items-center justify-center text-black/40" style={{ width: 192, height: 192 }}>
                        {busy ? <Loader2 size={40} className="animate-spin" /> : <QrCode size={64} />}
                      </div>}
                </div>
                {theCode && (
                  <>
                    <div className="text-[11px] font-semibold tracking-[0.28em] text-white/45 uppercase mt-4">Code</div>
                    <div className="font-extrabold tracking-[0.25em] text-3xl" style={{ color: accent.text }}>{theCode}</div>
                    <button onClick={copy}
                      className="mt-3 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold bg-white/[0.07] border border-white/10 hover:bg-white/[0.12] text-white/85 transition">
                      {copied ? <><Check size={13} style={{ color: accent.text }} /> Lien copié</> : <><Copy size={13} /> Copier le lien</>}
                    </button>
                  </>
                )}
              </>
            ) : (
              <>
                <p className="text-sm text-white/65 leading-relaxed mb-4">
                  Vise le <b>QR affiché sur la TV / l'ordi</b> — dès qu'il est reconnu,
                  tu pourras lui envoyer tes sons et vidéos.
                </p>
                {!scanErr
                  ? <ScanFrame accent={accent}><QrScanner onResult={onScan} onUnsupported={() => setScanErr(true)} /></ScanFrame>
                  : null}
                <button onClick={() => { setOpen(false); router.push('/app/join') }}
                  className="mt-4 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold bg-white/[0.07] border border-white/10 hover:bg-white/[0.12] text-white/85 transition">
                  <Keyboard size={13} /> Saisir le code à la main
                </button>
              </>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}

function ModeTab({ active, onClick, accent, icon, label }: {
  active: boolean; onClick: () => void; accent: { from: string; to: string }; icon: ReactNode; label: string
}) {
  return (
    <button onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-semibold transition ${active ? 'text-white shadow-[0_0_14px_rgb(var(--a2)/.5)]' : 'text-white/60 hover:text-white bg-white/[0.05] border border-white/10'}`}
      style={active ? { background: `linear-gradient(135deg, ${accent.from}, ${accent.to})` } : undefined}>
      {icon} {label}
    </button>
  )
}

/** Cadre de visée coloré autour du scanner caméra. */
function ScanFrame({ accent, children }: { accent: { text: string }; children: ReactNode }) {
  return (
    <div className="relative inline-block" style={{ ['--scan' as any]: accent.text }}>
      {children}
    </div>
  )
}
